import { SectionHeader } from "@/components/shared/CustomDivider";
import IconGenerator from "@/components/shared/IconGenerator";
import PrioritySelect, {
  TaskItemPriorityEnum,
} from "@/components/shared/PrioritySelect";
import {
  useRecapActionItemById,
  useRecapModifyActionItem,
} from "@/features/hooks/useRecap";
import { ActionItemProps } from "@/interfaces/IRecap";
import { UserOutlined } from "@ant-design/icons";
import { DatePicker, Icon, ResponsiveModal } from "@nexus/shared-ui";
import { Avatar, Button, Col, Flex, Form, Input, Row, Select } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

const getPriorityFormValue = (priority?: string | number | null) => {
  const numericPriority = Number(priority);

  return Number.isNaN(numericPriority) ||
    numericPriority === TaskItemPriorityEnum.Select
    ? undefined
    : numericPriority;
};

const ModifyActionItem = ({
  open,
  data,
  sessionId,
  onClose,
  participants,
}: ActionItemProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { data: actionItemDetail, isFetching: isFetchingActionItem } =
    useRecapActionItemById(data?.id);

  const { mutate: modifyActionItem, isPending } = useRecapModifyActionItem();

  const participantOptions = useMemo(() => {
    const options = participants?.data || [];

    const currentUser = actionItemDetail?.user || data?.user;

    if (
      currentUser?.id &&
      currentUser?.fullName &&
      !options.find((item: any) => item?.value === currentUser?.id)
    ) {
      options.push({
        label: currentUser?.fullName,
        value: currentUser?.id,
        userId: currentUser?.id,
        assigneeName: currentUser?.fullName,
        avatar:
          currentUser?.avatar ||
          currentUser?.profileImage ||
          currentUser?.picture ||
          currentUser?.image ||
          currentUser?.avatarUrl ||
          null,
      });
    }

    return options;
  }, [participants, actionItemDetail?.user, data?.user]);

  useEffect(() => {
    if (!open) return;

    if (data?.id) {
      form.setFieldsValue({
        title: data?.title || "",
        description: actionItemDetail?.description || data?.description || "",
        assigneeName: data?.user?.id || data?.assigneeName || "",
        priority: getPriorityFormValue(actionItemDetail?.priority),
        dueDate: actionItemDetail?.dueDate
          ? (actionItemDetail.dueDate as any)
          : null,
      });
    } else {
      form.resetFields();
    }
  }, [open, data, actionItemDetail, form]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const onSubmitForm = useCallback(
    (values: {
      title: string;
      description?: string;
      assigneeName: string;
      priority?: TaskItemPriorityEnum;
      dueDate?: string;
    }) => {
      modifyActionItem(
        {
          id: data?.id || undefined,
          participants: participantOptions,
          data: {
            sessionId,
            title: values?.title,
            description:
              values?.description ||
              actionItemDetail?.description ||
              data?.description ||
              values?.title,
            assigneeName: values?.assigneeName,
            dueDate: values?.dueDate,
            priority: values?.priority != null ? String(values.priority) : "",
            sourceTime: actionItemDetail?.sourceTime || "",
          },
        },
        {
          onSuccess: () => {
            handleClose();
          },
        }
      );
    },
    [
      modifyActionItem,
      sessionId,
      data,
      actionItemDetail,
      participantOptions,
      handleClose,
    ]
  );

  const title = data?.id ? t("recap.editActionItem") : t("recap.addActionItem");

  const formContent = useMemo(
    () => (
      <Form form={form} onFinish={onSubmitForm} colon={false}>
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item
              label={
                <IconGenerator
                  name="UilTextSize"
                  color="var(--color-disabled)"
                  size={20}
                />
              }
              name="title"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("recap.titleRequired"),
                },
              ]}
            >
              <Input placeholder={t("recap.enterTitle")} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="description"
              style={{ marginBottom: 8 }}
              label={
                <IconGenerator
                  name="UilParagraph"
                  color="var(--color-disabled)"
                  size={20}
                />
              }
            >
              <TextArea placeholder={t("actions.placeholder.description")} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <SectionHeader
              title={t("recap.keyInformation")}
              icon="UilKeySkeleton"
              iconColor="var(--color-disabled)"
            />
          </Col>

          <Col xs={24} md={12} lg={24}>
            <Form.Item
              name="assigneeName"
              label={
                <IconGenerator
                  name="UilUser"
                  color="var(--color-disabled)"
                  size={20}
                />
              }
            >
              <Select
                showSearch
                loading={isFetchingActionItem}
                placeholder={t("general.select")}
                options={participantOptions}
                optionFilterProp="label"
                optionRender={(option) => {
                  const data = (option as any)?.data || option;
                  const avatar = data?.user?.pictureUrlVm?.thumbnailUrl;
                  return (
                    <Flex align="center" gap={8}>
                      <Avatar
                        size={20}
                        src={avatar || undefined}
                        icon={!avatar ? <UserOutlined /> : undefined}
                      >
                        {!avatar && typeof data?.label === "string"
                          ? data.label[0]
                          : null}
                      </Avatar>
                      <span>{data?.label}</span>
                    </Flex>
                  );
                }}
                labelRender={({ label, value }) => {
                  const matched = participantOptions.find(
                    (item: any) => item?.value === value
                  );
                  const avatar = matched?.avatar;
                  return (
                    <Flex align="center" gap={8}>
                      <Avatar
                        size={20}
                        src={avatar || undefined}
                        icon={!avatar ? <UserOutlined /> : undefined}
                      >
                        {!avatar && typeof label === "string" ? label[0] : null}
                      </Avatar>
                      <span>{label}</span>
                    </Flex>
                  );
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={24}>
            <Form.Item
              name="dueDate"
              label={
                <IconGenerator
                  name="UilCalendar"
                  color="var(--color-disabled)"
                  size={20}
                />
              }
            >
              <DatePicker
                placeholder={t("actions.placeholder.duoDate")}
                key="dueDate"
                calendarType="jalali"
                allowClear
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={24}>
            <Form.Item
              name="priority"
              label={
                <IconGenerator
                  name="UilFlag"
                  color="var(--color-disabled)"
                  size={20}
                />
              }
            >
              <PrioritySelect allowClear />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    ),
    [form, onSubmitForm, isFetchingActionItem, participantOptions, t]
  );

  return (
    <ResponsiveModal
      open={open}
      title={title}
      onClose={handleClose}
      destroyOnHidden
      drawerProps={{
        placement: "right",
        width: "100%",
      }}
      drawerFooter={
        <Flex gap={8}>
          <Button
            className="w-100"
            type="primary"
            loading={isPending}
            disabled={!sessionId}
            icon={<Icon name="UilSave" size={16} color="white" />}
            onClick={() => form.submit()}
          >
            {t("actions.save")}
          </Button>

          <Button className="w-100" onClick={handleClose}>
            {t("actions.cancel")}
          </Button>
        </Flex>
      }
      modalProps={{
        footer: (
          <Flex justify="flex-end" gap={8}>
            <Button onClick={handleClose}>{t("actions.cancel")}</Button>
            <Button
              type="primary"
              loading={isPending}
              disabled={!sessionId}
              onClick={() => form.submit()}
            >
              {t("actions.save")}
            </Button>
          </Flex>
        ),
      }}
    >
      {formContent}
    </ResponsiveModal>
  );
};

export default ModifyActionItem;
